import axios from 'axios';
import type { SituacaoPrazo } from './dashboardOficinaApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const authHeader = (): Record<string, string> => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Consulta do setor de EVENTOS (at-85, ADR 0039) — "em que pé está este veículo?".
 *
 * A resposta é deliberadamente estreita: a esteira atual, a previsão dela e os dias na oficina.
 * Nada de proprietário, telefone, liberação, colaboradores, observações internas ou valores.
 * Quem precisar de mais que isto está na tela errada, não faltando campo aqui.
 *
 * ⚠️ Esta função NUNCA engole erro (at-60). Quem chama precisa distinguir "não há veículo em
 * atendimento" de "não consegui perguntar" — as duas coisas produzem lista vazia, e confundi-las
 * faz o setor concluir que a oficina está livre, ou que o veículo que procura já saiu.
 */
export interface VeiculoEmAndamento {
  id: string;
  plate: string;
  /** Nome da esteira atual, como aparece na tela ("Funilaria"). */
  esteiraNome: string | null;
  /**
   * Previsão TOTAL de dias para a esteira atual — **não** é "dias restantes".
   *
   * É o mesmo número que o Chefe de Oficina define ao mover o veículo. A tela precisa dizer isso
   * por extenso: lido como "faltam N dias", vira cobrança errada.
   */
  previsaoDiasNaEsteira: number | null;
  /** Dias desde a entrada do veículo na oficina. */
  diasNaOficina: number | null;
  entryDate: string | null;
  /**
   * at-99 (ADR 0050): os NOMES das esteiras abertas em paralelo — serviço em curso além da
   * etapa. Vazio na maioria dos veículos.
   *
   * 🔴 Só nomes: **sem** quem abriu, sem motivo e sem data. Autoria e texto interno são o que a
   * projeção mínima do papel (ADR 0039) mantém fora daqui — o mesmo raciocínio que faz o modal
   * deste setor esconder a autoria das sub-tarefas (at-89).
   *
   * Opcional para a tela sobreviver a um backend anterior à entrega.
   */
  esteirasParalelas?: string[];
  /**
   * at-134 (ADR 0064): o semáforo do prazo da etapa — a MESMA régua da aba Prazos do Dashboard
   * Oficina, calculada no backend. ⚠️ `SEM_PRAZO` é estado próprio, nunca "em dia".
   * Opcionais para a tela sobreviver a um backend anterior à entrega.
   */
  situacaoPrazo?: SituacaoPrazo;
  /** Há quantos dias o veículo está na etapa atual. */
  diasNaEtapa?: number | null;
  /** at-134: retorno de garantia? Só o fato e se o serviço original foi feito aqui. */
  posEvento?: { origem: 'INTERNO' | 'EXTERNO' } | null;
}

export interface RespostaSetorEventos {
  total: number;
  page: number;
  limit: number;
  itens: VeiculoEmAndamento[];
}

export const buscarVeiculosEmAndamento = async (params: {
  busca?: string;
  page?: number;
  limit?: number;
}): Promise<RespostaSetorEventos> => {
  const { data } = await axios.get<RespostaSetorEventos>(`${BACKEND_URL}/setor-eventos/veiculos`, {
    params: {
      ...(params.busca ? { busca: params.busca } : {}),
      page: params.page ?? 1,
      limit: params.limit ?? 20,
    },
    headers: authHeader(),
  });

  return data;
};
